<script setup lang="ts">
import { useChatStore, type ChatSession } from '@/stores/chat'
import { useAuthStore } from '@/stores/auth'
import { User, Clock, MessageCircle } from 'lucide-vue-next'

const chatStore = useChatStore()
const authStore = useAuthStore()

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  return `${hrs}h ago`
}

function isMySession(session: ChatSession): boolean {
  return session.agent_id === authStore.user?.id
}
</script>

<template>
  <div class="flex flex-col h-full overflow-hidden">
    <!-- Queued -->
    <div v-if="chatStore.queuedSessions.length" class="mb-2">
      <div class="px-3 py-1.5 text-xs font-semibold text-warning-400 uppercase tracking-wider">
        Queued ({{ chatStore.queuedSessions.length }})
      </div>
      <button
        v-for="session in chatStore.queuedSessions"
        :key="session.id"
        @click="chatStore.claimSession(session.id)"
        class="w-full px-3 py-2.5 text-left hover:bg-surface-700/50 transition-colors border-l-2 border-warning-500"
      >
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2 min-w-0">
            <User class="w-4 h-4 text-surface-400 flex-shrink-0" />
            <span class="text-sm text-surface-200 truncate">{{ session.visitor_name || 'Visitor' }}</span>
          </div>
          <span class="text-xs text-surface-500 flex-shrink-0">{{ timeAgo(session.created_at) }}</span>
        </div>
        <div class="text-xs text-surface-500 mt-0.5 truncate">{{ session.client_name || 'Unknown client' }}</div>
      </button>
    </div>

    <!-- My Active -->
    <div v-if="chatStore.mySessions.length" class="mb-2">
      <div class="px-3 py-1.5 text-xs font-semibold text-success-400 uppercase tracking-wider">
        My Chats ({{ chatStore.mySessions.length }})
      </div>
      <button
        v-for="session in chatStore.mySessions"
        :key="session.id"
        @click="chatStore.selectSession(session.id)"
        class="w-full px-3 py-2.5 text-left hover:bg-surface-700/50 transition-colors border-l-2"
        :class="chatStore.selectedSessionId === session.id ? 'border-primary-500 bg-surface-700/30' : 'border-success-500'"
      >
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2 min-w-0">
            <MessageCircle class="w-4 h-4 text-success-400 flex-shrink-0" />
            <span class="text-sm text-surface-200 truncate">{{ session.visitor_name || 'Visitor' }}</span>
          </div>
          <span class="text-xs text-surface-500 flex-shrink-0">{{ timeAgo(session.created_at) }}</span>
        </div>
        <div class="text-xs text-surface-500 mt-0.5 truncate">{{ session.client_name || 'Unknown client' }}</div>
      </button>
    </div>

    <!-- Other Active (other agents) -->
    <div v-if="chatStore.activeSessions.filter(s => !isMySession(s)).length" class="mb-2">
      <div class="px-3 py-1.5 text-xs font-semibold text-surface-500 uppercase tracking-wider">
        Other Active
      </div>
      <div
        v-for="session in chatStore.activeSessions.filter(s => !isMySession(s))"
        :key="session.id"
        class="px-3 py-2.5 border-l-2 border-surface-600"
      >
        <div class="flex items-center justify-between">
          <span class="text-sm text-surface-400 truncate">{{ session.visitor_name || 'Visitor' }}</span>
          <span class="text-xs text-surface-600">{{ session.agent_first_name || 'Agent' }}</span>
        </div>
        <div class="text-xs text-surface-600 mt-0.5 truncate">{{ session.client_name || 'Unknown client' }}</div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="!chatStore.sessions.length && !chatStore.loading" class="flex-1 flex items-center justify-center">
      <div class="text-center text-surface-500">
        <MessageCircle class="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p class="text-sm">No active chats</p>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="chatStore.loading && !chatStore.sessions.length" class="flex-1 flex items-center justify-center">
      <div class="text-sm text-surface-500">Loading...</div>
    </div>
  </div>
</template>
